import { FieldNamesMarkedBoolean } from 'react-hook-form';

import { FieldTypes } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { DEFAULT_CREATIVES_QUANTITY } from '@features/campaigns/builder/consts/common.ts';
import { UpdateCampaignFormStep03Values } from '@features/campaigns/builder/containers/FormPages/FormPages.types';
import { Creative } from '@features/campaigns/builder/services/campaign/uploadCreatives.ts';
import {
  CampaignDetails,
  CampaignMediaType,
  CampaignMediaTypes,
  CampaignStatus,
  CampaignType,
  CampaignTypes,
  CREATIVE_FILE_TYPES,
  CreativeServices,
  DisplayAdProduct,
  InvalidSectionsItem,
} from '@features/campaigns/shared/types/campaign.ts';
import {
  isAdProductDisplayNonNull,
  isAdProductVideoInternal,
  isAdProductVideoNonNull,
} from '@features/campaigns/shared/types/typeGuards.ts';
import { ValueOf } from '@shared/types/common';

export interface UpdateCampaignStep03CrossPayload {
  vod: true;
  linearAddressable: boolean;
}

export interface UpdateCampaignStep03DisplayPayload {
  type?: ValueOf<typeof DisplayAdProduct>;
  creativeServices?: ValueOf<typeof CreativeServices>;
  numberOfCreatives?: number;
}

export interface UpdateCampaignStep03VideoPayload {
  type?: CampaignType;
  vod?: boolean;
  linearAddressable?: boolean;
}

export interface UpdateCampaignStep03Payload {
  ctv?: UpdateCampaignStep03CTVPayload;
  cross?: UpdateCampaignStep03CrossPayload;
}

export interface UpdateCampaignStep03CTVPayload {
  display?: UpdateCampaignStep03DisplayPayload;
  video?: UpdateCampaignStep03VideoPayload;
}

function step03VideoPayloadComposer(
  formValues: UpdateCampaignFormStep03Values,
  campaignType: CampaignType,
): UpdateCampaignStep03VideoPayload | undefined {
  const { formatTypeVideo, videoProductsLinearAddressable } = formValues;
  const isCampaignExternal = campaignType === CampaignTypes.SELF_SERVICE;

  if (isCampaignExternal) {
    return undefined;
  }

  if (!formatTypeVideo) {
    return {};
  }

  return {
    type: campaignType,
    vod: true,
    linearAddressable: videoProductsLinearAddressable,
  };
}

function step03DisplayPayloadComposer(
  formValues: UpdateCampaignFormStep03Values,
  campaignType: CampaignType,
): UpdateCampaignStep03DisplayPayload {
  const { formatTypeDisplay, isCreativeServicesFull, numberOfCreatives } = formValues;
  const isCampaignInternal = campaignType === CampaignTypes.INTERNAL;

  if (!formatTypeDisplay) {
    return {};
  }

  if (isCampaignInternal) {
    if (isCreativeServicesFull) {
      return {
        type: DisplayAdProduct.INTERNAL,
        creativeServices: CreativeServices.FULL,
        numberOfCreatives: numberOfCreatives || DEFAULT_CREATIVES_QUANTITY,
      };
    }

    return {
      type: DisplayAdProduct.INTERNAL,
      creativeServices: CreativeServices.LIGHT,
    };
  }

  return {
    type: DisplayAdProduct.SELF_SERVICE,
  };
}

interface UpdateCampaignStep03DTOOptions {
  formValues: UpdateCampaignFormStep03Values;
  dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<UpdateCampaignFormStep03Values>>>;
  isDataAlreadySaved: boolean;
  campaignType: CampaignType;
  mediaType: CampaignMediaType;
}

const VideoFieldTypes = [FieldTypes.FORMAT_TYPE_VIDEO, FieldTypes.VIDEO_PRODUCTS_LINEAR_ADDRESSABLE] as const;
const DisplayFieldTypes = [
  FieldTypes.FORMAT_TYPE_DISPLAY,
  FieldTypes.IS_CREATIVE_SERVICES_FULL,
  FieldTypes.NUMBER_OF_CREATIVES,
] as const;

export function updateCampaignStep03DTO({
  campaignType,
  dirtyFields,
  formValues,
  isDataAlreadySaved,
  mediaType,
}: UpdateCampaignStep03DTOOptions): UpdateCampaignStep03Payload {
  if (mediaType === CampaignMediaTypes.CROSS) {
    return {
      cross: {
        vod: true,
        linearAddressable: Boolean(formValues.videoProductsLinearAddressable),
      },
      ctv: undefined,
    };
  }

  const hasVideoFieldsChanged = VideoFieldTypes.some((videoField) => videoField in dirtyFields);
  const hasDisplayFieldsChanged = DisplayFieldTypes.some((displayField) => displayField in dirtyFields);

  const video =
    hasVideoFieldsChanged || !isDataAlreadySaved ? step03VideoPayloadComposer(formValues, campaignType) : undefined;
  const display =
    hasDisplayFieldsChanged || !isDataAlreadySaved ? step03DisplayPayloadComposer(formValues, campaignType) : undefined;

  return {
    ctv: {
      display,
      video,
    },
    cross: undefined,
  };
}

interface UploadCreativesPayload {
  [CREATIVE_FILE_TYPES.LSHAPE]?: Creative;
  [CREATIVE_FILE_TYPES.LANDING]?: Creative;
}

function fileCanBeUploaded(file: File | 'string'): boolean {
  return file && typeof file !== 'string';
}
export function uploadCreativesDTO(formValues: UpdateCampaignFormStep03Values): UploadCreativesPayload {
  const { creativeImageLShaped, creativeImageLanding } = formValues;
  const uploadedLshapeImage = fileCanBeUploaded(creativeImageLShaped) ? creativeImageLShaped[0] : null;
  const uploadedLandingImage = fileCanBeUploaded(creativeImageLanding) ? creativeImageLanding[0] : null;

  const schema = {
    [CREATIVE_FILE_TYPES.LSHAPE]: { file: uploadedLshapeImage },
    [CREATIVE_FILE_TYPES.LANDING]: { file: uploadedLandingImage },
  };

  return Object.fromEntries(
    Object.entries(schema).filter(([, value]) => {
      return !!value.file;
    }),
  );
}

export interface Step03FormData {
  [FieldTypes.FORMAT_TYPE_VIDEO]: boolean;
  [FieldTypes.FORMAT_TYPE_DISPLAY]: boolean;
  [FieldTypes.VIDEO_PRODUCTS_LINEAR_ADDRESSABLE]: boolean;
  [FieldTypes.NUMBER_OF_CREATIVES]: number;
  [FieldTypes.IS_CREATIVE_SERVICES_FULL]: boolean;
  [FieldTypes.CREATIVE_L_SHAPED_IMAGE]: string | null;
  [FieldTypes.CREATIVE_LANDING_IMAGE]: string | null;
}

export interface RestoredStep03Data {
  formData: Step03FormData | undefined;
  invalidSections?: InvalidSectionsItem[];
  status: CampaignStatus;
}

export function restoreStep03DataDTO(data: CampaignDetails): RestoredStep03Data {
  const isFormatTypeVideoAvailable = isAdProductVideoNonNull(data.adProductVideo);
  const isFormatTypeDisplayAvailable = isAdProductDisplayNonNull(data.adProductDisplay);

  if (!isFormatTypeVideoAvailable && !isFormatTypeDisplayAvailable) {
    return {
      formData: undefined,
      invalidSections: data.invalidSections,
      status: data.status,
    };
  }

  const isLinearAddressableAvailable = isAdProductVideoInternal(data.adProductVideo)
    ? data.adProductVideo.data.linearAddressable
    : true;

  const numberOfCreativesAvailable =
    isAdProductDisplayNonNull(data.adProductDisplay) && data.adProductDisplay.data.numberOfCreatives !== null
      ? data.adProductDisplay.data.numberOfCreatives
      : DEFAULT_CREATIVES_QUANTITY;

  const isCreativeServicesFullAvailable = isAdProductDisplayNonNull(data.adProductDisplay)
    ? data.adProductDisplay.data.creativeServices === CreativeServices.FULL
    : true;

  const lShapedImageUrl = isAdProductDisplayNonNull(data.adProductDisplay)
    ? data.adProductDisplay.lShapeImageUrl
    : null;

  const landingImageUrl = isAdProductDisplayNonNull(data.adProductDisplay)
    ? data.adProductDisplay.landingImageUrl
    : null;

  return {
    formData: {
      [FieldTypes.FORMAT_TYPE_VIDEO]: isFormatTypeVideoAvailable,
      [FieldTypes.FORMAT_TYPE_DISPLAY]: isFormatTypeDisplayAvailable,
      [FieldTypes.VIDEO_PRODUCTS_LINEAR_ADDRESSABLE]: isLinearAddressableAvailable,
      [FieldTypes.NUMBER_OF_CREATIVES]: numberOfCreativesAvailable,
      [FieldTypes.IS_CREATIVE_SERVICES_FULL]: isCreativeServicesFullAvailable,
      [FieldTypes.CREATIVE_L_SHAPED_IMAGE]: lShapedImageUrl,
      [FieldTypes.CREATIVE_LANDING_IMAGE]: landingImageUrl,
    },
    invalidSections: data.invalidSections,
    status: data.status,
  };
}
